import { useEffect, useState } from 'react';
import useDominantSpeaker from '../useDominantSpeaker/useDominantSpeaker';
import { useAppState } from '../../state';
import type { AgoraParticipantLike } from '../../types';
import { sameAgoraParticipant } from '../../utils/sameAgoraParticipant';
import useParticipants from '../useParticipants/useParticipants';

interface OrderedParticipant {
  participant: AgoraParticipantLike;
  dominantSpeakerStartTime: number;
}

export default function useGalleryViewParticipants(isMobileGalleryViewActive = false): AgoraParticipantLike[] {
  const participants = useParticipants();
  const dominantSpeaker = useDominantSpeaker();
  const { maxGalleryViewParticipants } = useAppState();
  const [orderedParticipants, setOrderedParticipants] = useState<OrderedParticipant[]>(
    participants
      .map((p: AgoraParticipantLike) => ({ participant: p, dominantSpeakerStartTime: 0 }))
      .filter((row): row is OrderedParticipant => row.participant != null)
  );

  useEffect(() => {
    if (dominantSpeaker !== null) {
      setOrderedParticipants(prev => {
        const next = prev.slice();
        const idx = next.findIndex(p => sameAgoraParticipant(p.participant, dominantSpeaker));
        if (idx >= 0) next[idx].dominantSpeakerStartTime = Date.now();
        const maxFirst = isMobileGalleryViewActive ? 5 : maxGalleryViewParticipants - 1;
        // No "first page" slots to reorder (e.g. maxGalleryViewParticipants is 1, or localStorage is 0)
        if (maxFirst < 1) {
          return next;
        }
        const firstPage = next.slice(0, maxFirst);
        if (!firstPage.some(p => sameAgoraParticipant(p.participant, dominantSpeaker))) {
          const sorted = [...firstPage].sort((a, b) => a.dominantSpeakerStartTime - b.dominantSpeakerStartTime);
          const least = sorted[0];
          if (!least) {
            return next;
          }
          const domIdx = next.findIndex(p => sameAgoraParticipant(p.participant, dominantSpeaker));
          const leastIdxBefore = next.findIndex(p => p === least);
          if (domIdx < 0 || leastIdxBefore < 0) {
            return next;
          }
          next.splice(domIdx, 1);
          const leastIdxAfter = domIdx < leastIdxBefore ? leastIdxBefore - 1 : leastIdxBefore;
          next.splice(leastIdxAfter, 1, { participant: dominantSpeaker, dominantSpeakerStartTime: Date.now() });
          next.splice(maxFirst, 0, least);
        }
        return next;
      });
    }
  }, [dominantSpeaker, maxGalleryViewParticipants, isMobileGalleryViewActive]);

  useEffect(() => {
    setOrderedParticipants(
      participants
        .map((p: AgoraParticipantLike) => ({ participant: p, dominantSpeakerStartTime: 0 }))
        .filter((row): row is OrderedParticipant => row.participant != null)
    );
  }, [participants]);

  return orderedParticipants.filter(p => p?.participant != null).map(p => p.participant);
}
